/**
 * Protocolo compartido entre cliente y servidor de la ruleta.
 *
 * Define la configuración de la partida, las vistas (el estado que el servidor
 * manda al cliente para que lo pinte) y los mensajes que viajan por el
 * WebSocket. El servidor es la única fuente de verdad: el cliente pinta lo que
 * recibe y manda intenciones.
 */

import type { Letter } from './letters.js';
import type { LetterPosition, PanelView } from './panel.js';

// --- Configuración de la partida (la fija el anfitrión en el vestíbulo) ------

/**
 * Cómo se para la rueda.
 * - `auto`: se tira y cae donde caiga. Azar puro, sin barrera de reflejos.
 * - `manual`: el tictac recorre las casillas y el jugador pulsa para parar.
 *   Da control y suena bien, pero premia reflejos; por eso no es lo normal.
 */
export type WheelStop = 'auto' | 'manual';

/**
 * Variante de la prueba final.
 * - `noClock`: sin cronómetro, con un número limitado de intentos. Es lo justo
 *   con lector de pantalla, que necesita oír el panel antes de contestar.
 * - `clock`: contra reloj, como en el concurso.
 */
export type FinalMode = 'noClock' | 'clock';

/** Longitud de las frases que salen en el panel. */
export type PanelLength = 'short' | 'medium' | 'long';

/**
 * Cuánto detalle usa el juego al leer el panel.
 * - `grouped`: «dos huecos, A, hueco». Lo normal: corto y se sigue de oído.
 * - `cell`: casilla a casilla, para quien quiera precisión absoluta.
 *
 * No forma parte de la configuración de la partida: es de cada jugador y se
 * guarda en su navegador. En la misma mesa puede haber quien necesite el detalle
 * y quien lo encuentre insufrible, y no tiene por qué ganar el anfitrión.
 */
export type PanelVerbosity = 'grouped' | 'cell';

/** Dificultad de un jugador manejado por la máquina. */
export type BotDifficulty = 'facil' | 'media' | 'dificil';

/** Catálogo de dificultades, para pintar los botones del vestíbulo. */
export const BOT_DIFFICULTIES: readonly { id: BotDifficulty; label: string }[] = [
  { id: 'facil', label: 'Fácil' },
  { id: 'media', label: 'Media' },
  { id: 'dificil', label: 'Difícil' },
];

/** Configuración completa de una partida, editable en el vestíbulo. */
export interface GameConfig {
  /** Rondas normales antes de la prueba final. */
  rounds: number;
  finalMode: FinalMode;
  /** Segundos de la prueba final cuando `finalMode` es `clock`. */
  finalSeconds: number;
  wheelStop: WheelStop;
  /** Lo que cuesta comprar una vocal. */
  vowelPrice: number;
  panelLength: PanelLength;
  /**
   * Packs temáticos activos, por identificador. Vacío es solo el banco base.
   *
   * Van desactivados por defecto a propósito: un panel de un tema que no
   * conoces no es difícil, es imposible, y eso no se arregla escuchando mejor.
   */
  packs: string[];
}

/** Configuración por defecto: la más accesible de cada opción. */
export const DEFAULT_CONFIG: GameConfig = {
  rounds: 3,
  finalMode: 'noClock',
  finalSeconds: 40,
  wheelStop: 'auto',
  vowelPrice: 150,
  panelLength: 'medium',
  packs: [],
};

/** Límites de las opciones numéricas, para validarlas en el servidor. */
export const CONFIG_LIMITS = {
  rounds: { min: 1, max: 8 },
  finalSeconds: { min: 20, max: 90 },
  vowelPrice: { min: 0, max: 500 },
} as const;

// --- Vistas del estado -------------------------------------------------------

/** Fase en la que se encuentra la partida. */
export type GamePhase = 'lobby' | 'playing' | 'gameOver';

/** Vista pública de un jugador. */
export interface PlayerView {
  id: string;
  name: string;
  isBot: boolean;
  connected: boolean;
  /** Solo en bots: su dificultad. */
  difficulty?: BotDifficulty;
  /** Dinero ganado en rondas ya cerradas. */
  bank: number;
  /** Dinero acumulado en la ronda en curso; se pierde al quebrar. */
  pot: number;
  /** true si tiene un comodín sin gastar. */
  wildcard: boolean;
  /** true si tiene un sobre sin abrir (su premio no se enseña hasta abrirlo). */
  surprise: boolean;
  /** true si su próxima cantidad vale el doble. */
  doubler: boolean;
  /** true si está en juego por el bote. */
  jackpot: boolean;
}

/**
 * Vista pública de la ronda en curso.
 *
 * No contiene la frase: el panel viaja con las casillas ocultas en blanco, así
 * que la solución no sale del servidor hasta que alguien la acierta o la ronda
 * termina.
 */
export interface RoundView {
  /** Número de ronda (1..rounds); `null` en la prueba final. */
  index: number | null;
  /** true si esta es la prueba final. */
  isFinal: boolean;
  panel: PanelView;
  /** Jugador al que le toca actuar. */
  turnPlayerId: string;
  /** Qué se espera de él ahora mismo. */
  turn: TurnView;
  /** Bote acumulado de la partida. */
  jackpot: number;
  /**
   * Letras regaladas de la prueba final, ya descubiertas; vacío en las rondas
   * normales. Se guardan aparte de `panel.used` para poder decir cuáles fueron
   * un regalo y cuáles eligió el finalista.
   */
  freeLetters: Letter[];
  /**
   * Letras que el finalista ya ha elegido y todavía no se han descubierto. Son
   * públicas: se cantan al elegirlas, como en el concurso. Sirven para no
   * ofrecérselas otra vez.
   */
  finalPicks: Letter[];
  /**
   * Instante (epoch ms) en que se acaba el tiempo de la prueba final contra
   * reloj; null si no hay reloj.
   *
   * El cliente calcula con esto lo que queda **cuando se lo piden**. No es una
   * región `aria-live` ni se refresca solo: un reloj que se reescribe deja al
   * lector de pantalla hablando sin parar y tapa el juego entero.
   */
  deadline: number | null;
}

/**
 * Qué tiene que hacer el jugador de turno. El cliente pinta unos mandos u otros
 * según esto, y lo anuncia al empezar el turno.
 *
 * - `choosing`: elige entre tirar, comprar vocal, gastar comodín o resolver.
 * - `consonant`: ha caído en una casilla que se cobra por letra y debe pedir
 *   una consonante; cada aparición vale `value`.
 * - `freeVowel`: ha caído en «vocal gratis» y elige una sin pagarla.
 * - `penalty`: le ha caído un castigo y tiene comodín, así que decide si lo
 *   gasta para anularlo.
 * - `express`: está en la casilla exprés, pidiendo consonantes a `value` hasta
 *   que se plante o falle.
 */
export type TurnView =
  | { kind: 'choosing' }
  | { kind: 'consonant'; value: number }
  | { kind: 'freeVowel' }
  | { kind: 'penalty'; wedge: 'bankrupt' | 'loseTurn'; label: string }
  | { kind: 'express'; value: number }
  /**
   * La rueda está girando y hay que pararla (modo «a mano»). `endsAt` es cuándo
   * se pararía sola: nadie se queda colgado por no reaccionar a tiempo.
   */
  | { kind: 'spinning'; endsAt: number }
  /**
   * Prueba final: está eligiendo sus letras. Se guardan sin enseñarlas y se
   * descubren todas de golpe al terminar, como en el concurso; enseñarlas una a
   * una convertiría la elección en un juego distinto y más fácil.
   */
  | { kind: 'finalLetters'; consonants: number; vowels: number }
  /**
   * Prueba final: toca decir la frase. `attemptsLeft` es null cuando la partida
   * se juega contra reloj, porque entonces el límite es el tiempo.
   */
  | { kind: 'finalSolve'; attemptsLeft: number | null };

/** Un pack temático disponible, tal y como se ofrece en el vestíbulo. */
export interface PackView {
  id: string;
  name: string;
  /** Frases que aporta, para saber cuánto engorda el banco. */
  phrases: number;
}

/** Vista pública completa del estado de la sala. */
export interface GameView {
  /** Packs temáticos que tiene el servidor, se usen o no. */
  packs: PackView[];
  roomCode: string;
  phase: GamePhase;
  /** Quién creó la sala; solo esa persona configura y empieza la partida. */
  hostId: string | null;
  players: PlayerView[];
  config: GameConfig;
  /** Ronda en curso mientras se juega; null en el vestíbulo y al terminar. */
  round: RoundView | null;
}

// --- Eventos puntuales (disparan sonido y anuncio, no pintan estado) ---------

/**
 * Algo que acaba de pasar. A diferencia de la vista, que describe cómo están las
 * cosas ahora, un evento se pierde si no se anuncia al llegar.
 */
export type GameEvent =
  | { kind: 'playerJoined'; playerId: string; name: string }
  | { kind: 'playerLeft'; playerId: string; name: string }
  | { kind: 'gameStarted' }
  | { kind: 'roundStarted'; index: number | null; isFinal: boolean; category: string }
  | { kind: 'turnStarted'; playerId: string }
  /** La rueda empieza a girar y hay que pararla (modo «a mano»). */
  | { kind: 'wheelStarted'; playerId: string; endsAt: number }
  /** La rueda ha parado. `index` es la posición dentro de la rueda. */
  | { kind: 'spun'; playerId: string; index: number; label: string }
  /** Se ha pedido una letra y estaba (o no) en el panel. */
  | { kind: 'letterAsked'; playerId: string; letter: Letter; count: number; earned: number; positions: LetterPosition[] }
  /** Se ha pedido una letra que ya se había pedido; no cuenta. */
  | { kind: 'letterRepeated'; playerId: string; letter: Letter }
  /** Vocal elegida; `price` es 0 si venía de la casilla de vocal gratis. */
  | { kind: 'vowelTaken'; playerId: string; letter: Letter; price: number; count: number; positions: LetterPosition[] }
  | { kind: 'bankrupt'; playerId: string; lost: number }
  /** El turno pasa a otro jugador, y por qué. */
  | { kind: 'turnLost'; playerId: string; reason: 'wedge' | 'missingLetter' | 'wrongSolution' | 'missingVowel' }
  | { kind: 'wildcardGained'; playerId: string }
  /** Comodín gastado: para librarse de un castigo o para pedir consonante extra. */
  | { kind: 'wildcardSpent'; playerId: string; use: 'save' | 'consonant' }
  | { kind: 'doublerGained'; playerId: string }
  | { kind: 'doublerUsed'; playerId: string; value: number }
  | { kind: 'surpriseGained'; playerId: string }
  | { kind: 'jackpotEntered'; playerId: string; jackpot: number }
  | { kind: 'expressStarted'; playerId: string; value: number }
  | { kind: 'expressStopped'; playerId: string }
  /** Empieza la prueba final: quién la juega, con qué letras de regalo y por cuánto. */
  | { kind: 'finalStarted'; playerId: string; freeLetters: Letter[]; prize: number }
  /** El finalista ha elegido una letra. Todavía no se sabe si está. */
  | { kind: 'finalLetterPicked'; playerId: string; letter: Letter; consonantsLeft: number; vowelsLeft: number }
  /** Se descubren de golpe las letras elegidas. */
  | { kind: 'finalRevealed'; letters: Letter[]; found: Letter[] }
  /** Se acabó el tiempo de la prueba final. */
  | { kind: 'finalTimeUp' }
  /** Nada que hacer con esa casilla (vocal gratis sin vocales, comodín repetido…). */
  | { kind: 'nothingHappened'; playerId: string; reason: string }
  | { kind: 'solutionFailed'; playerId: string; text: string }
  /** Panel resuelto: se dice la frase y lo que se lleva quien la ha dicho. */
  | { kind: 'solved'; playerId: string; phrase: string; won: number; jackpot: number; surprise: string | null }
  /** Ronda cerrada. `solvedBy` es null si nadie la resolvió. */
  | { kind: 'roundEnded'; phrase: string; solvedBy: string | null }
  | { kind: 'gameOver'; winnerId: string | null };

// --- Mensajes del cliente al servidor ---------------------------------------

export type ClientMessage =
  | { type: 'join'; roomCode: string; name: string }
  | { type: 'start' }
  | { type: 'addBot'; difficulty: BotDifficulty }
  | { type: 'removeBot'; playerId: string }
  | { type: 'setConfig'; config: Partial<GameConfig> }
  | { type: 'spin' }
  /** Parar la rueda que está girando (modo «a mano»). */
  | { type: 'stopWheel' }
  /** Consonante pedida tras una tirada, o dentro del exprés. */
  | { type: 'consonant'; letter: Letter }
  /** Vocal: se paga, salvo que venga de la casilla de vocal gratis. */
  | { type: 'vowel'; letter: Letter }
  /** Gastar el comodín para pedir una consonante extra sin tirar. */
  | { type: 'wildcardConsonant' }
  /** Responder a la pregunta del comodín ante un castigo. */
  | { type: 'wildcardSave'; spend: boolean }
  /** Plantarse en el exprés y volver a elegir acción. */
  | { type: 'stopExpress' }
  | { type: 'solve'; text: string };

// --- Mensajes del servidor al cliente ---------------------------------------

export type ServerMessage =
  | { type: 'joined'; playerId: string }
  | { type: 'state'; state: GameView }
  | { type: 'event'; event: GameEvent }
  | { type: 'error'; message: string };
